import React, { useEffect, useState } from 'react';
import settingsApi, { SiteSettings as SiteSettingsModel } from '../../../services/settingsApi';

export default function AdminSiteSettings() {
  const [settings, setSettings] = useState<SiteSettingsModel | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);

  useEffect(() => {
    settingsApi.getSiteSettings()
      .then((data) => {
        setSettings(data);
        // detect read-only fallback (when server lacks endpoint)
        if ((data as any).__readonly) {
          setReadOnly(true);
          setMessage('Using default values (server settings endpoint not available).');
        }
      })
      .catch((e) => {
        console.error(e);
        setMessage('Failed to load settings');
      });
  }, []);

  const onChange = (field: keyof SiteSettingsModel) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!settings) return;
    setSettings({ ...settings, [field]: e.target.value });
  };

  const onSave = async () => {
    if (!settings) return;
    setSaving(true);
    setMessage(null);
    try {
      const updated = await settingsApi.updateSiteSettings(settings);
      setSettings(updated);
      setMessage('Settings saved');
    } catch (e) {
      console.error(e);
      setMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <div className="text-gray-500 dark:text-gray-400">Loading site settings…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Site Settings</h3>
        <p className="text-sm text-gray-500">These values are used in emails and links.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-1">Site URL</label>
          <input
            type="url"
            value={settings.siteUrl}
            onChange={onChange('siteUrl')}
            placeholder="https://threatcompass.net"
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          <p className="text-xs text-gray-500 mt-1">Used for verification, settings, and unsubscribe links.</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Site Name</label>
          <input
            type="text"
            value={settings.siteName}
            onChange={onChange('siteName')}
            placeholder="ThreatCompass"
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Email From Address</label>
          <input
            type="email"
            value={settings.emailFromAddress}
            onChange={onChange('emailFromAddress')}
            placeholder="alerts@threatcompass.net"
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Email From Name</label>
          <input
            type="text"
            value={settings.emailFromName}
            onChange={onChange('emailFromName')}
            placeholder="ThreatCompass"
            className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving || readOnly}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title={readOnly ? 'Endpoint not available in this environment' : undefined}
        >
          {saving ? 'Saving…' : readOnly ? 'Read-only (no endpoint)' : 'Save Settings'}
        </button>
        {message && <span className="text-sm text-gray-600 dark:text-gray-300">{message}</span>}
      </div>
    </div>
  );
}
